import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { isWhitespace } from "src/components";
import { configuration } from "src/configuration";
import { IssuePrivacyType, IssueStatus } from "src/constants";
import { useSignalR } from "src/hooks";
import {
  queryKeys,
  CreateIssueDto,
  FetchError,
  GetIssuesDto,
  IssueSummaryDto,
  optimisticallyUpdateQueryData,
  useAccessToken,
} from "src/http";
import { IUser } from "src/models";

type Variables = {
  newIssueGuid: string;
  teamName?: string;
  teamSlug?: string;
  createdByUser: IUser;
  collaborators: IUser[];
  description: string;
  impact: string;
  contextActionItemGuid?: string;
  createdFromSurveyResponseAnswerGuid?: string;
  privacy: IssuePrivacyType;
};

const useCreateIssueRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function createIssueRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();
    const body: CreateIssueDto = {
      issueGuid: variables.newIssueGuid,
      teamSlug: variables.teamSlug,
      description: variables.description,
      impact: variables.impact,
      contextActionItemGuid: variables.contextActionItemGuid,
      createdFromSurveyResponseAnswerGuid: variables.createdFromSurveyResponseAnswerGuid,
      collaboratorsUserIds: variables.collaborators.map((x) => x.userId),
      privacy: variables.privacy,
    };

    const response = await fetch(`${configuration.apiRootUrl}/issues`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { createIssueRequest };
};

const useCreateIssue = () => {
  const { createIssueRequest } = useCreateIssueRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: createIssueRequest,
    onMutate: async (variables) => {
      const newIssue: IssueSummaryDto = {
        guid: variables.newIssueGuid,
        assignedTeam:
          variables.teamSlug && variables.teamName ? { slug: variables.teamSlug, name: variables.teamName } : undefined,
        description: variables.description,
        currentStatus: IssueStatus.Open,
        createdByUser: variables.createdByUser,
        decisionMaker: null,
        collaborators: variables.collaborators,
        createdDateTimeUtc: DateTime.now().toUTC().toISO({ includeOffset: false }),
        lastModifiedDateTimeUtc: DateTime.now().toUTC().toISO({ includeOffset: false }),
        upvotes: [],
        hasImpact: !isWhitespace(variables.impact),
        hasDecision: false,
        proposalCount: 0,
        taskCount: 0,
        commentCount: 0,
        permissions: {
          userCanEditDescription: true,
          userCanEditContext: true,
          userCanEditImpact: true,
          userCanEditDecision: true,
          userCanEditDecisionMaker: true,
          userCanEditCollaborators: true,
          userCanEditTeam: true,
          userCanEditStatus: true,
          userCanDeleteIssue: true,
        },
        privacy: variables.privacy,
      };

      const cachedTeamData = await optimisticallyUpdateQueryData<GetIssuesDto>(
        queryClient,
        queryKeys.issues.getIssues(variables.teamSlug, undefined),
        (cachedData) => ({
          ...cachedData,
          issues: [...(cachedData.issues ?? []), newIssue],
        }),
      );

      const cachedUserData = await optimisticallyUpdateQueryData<GetIssuesDto>(
        queryClient,
        queryKeys.issues.getIssues(undefined, variables.createdByUser.userId),
        (cachedData) => ({
          ...cachedData,
          issues: [...(cachedData.issues ?? []), newIssue],
        }),
      );

      return { cachedTeamData, cachedUserData };
    },
    onSuccess: (_data, variables) => {
      signalR.sendRefetchUserIssues(variables.createdByUser.userId);

      if (variables.teamSlug) {
        signalR.sendRefetchTeamIssues(variables.teamSlug);
      }

      if (variables.contextActionItemGuid) {
        signalR.sendRefetchActionItem(variables.contextActionItemGuid);
      }
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(queryKeys.issues.getIssues(variables.teamSlug, undefined), context?.cachedTeamData);
      queryClient.setQueryData(
        queryKeys.issues.getIssues(undefined, variables.createdByUser.userId),
        context?.cachedUserData,
      );
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.issues.getIssues(variables.teamSlug, undefined));
      queryClient.invalidateQueries(queryKeys.issues.getIssues(undefined, variables.createdByUser.userId));

      // Update supporting items list
      if (variables.contextActionItemGuid) {
        queryClient.invalidateQueries(queryKeys.actionItems.item(variables.contextActionItemGuid));
      }

      if (variables.createdFromSurveyResponseAnswerGuid) {
        queryClient.invalidateQueries(
          queryKeys.surveys.getItemsCreatedFromResponseAnswer(variables.createdFromSurveyResponseAnswerGuid),
        );
      }
    },
  });
};

export { useCreateIssue };
